/**
 * MapScroll Module — Basemap + SVG Overlay with Scroll-Driven Zoom/Pan
 *
 * Scroll-driven animation for illustrated maps. Combines raster basemap
 * layers (WebP) with an inline SVG overlay for labels, highlights, and
 * info panels. Zoom/pan controlled via CSS transform on a zoomwrap.
 *
 * Data Attributes (Container panel):
 *   data-mapscroll              Mode flag (activates this module)
 *   data-ms-basemap="u1,u2"    Basemap image URLs (comma-separated, bottom→top)
 *   data-ms-natural="W,H"      Natural dimensions (SVG viewBox width,height)
 *
 * Data Attributes (Step panels):
 *   data-ms-step="N"           Step number
 *   data-ms-layers="id1,id2"   SVG layer IDs to show (all others hidden)
 *   data-ms-focus="X,Y,S"     Focus point: x,y in SVG coords + scale (empty = overview)
 *   data-ms-mobile-focus="X,Y,S"  Mobile override focus (optional, used on <=768px)
 *   data-ms-transition="0.8"  Custom transition duration (seconds, optional)
 *   data-ms-layers-delayed="id"  Layer IDs to show after a delay (optional)
 *   data-ms-delay="2000"          Delay in ms for delayed layers (default: 2000)
 *
 * Events: Listens to story:panel:activated
 * Registration: core.registerModule('mapscroll', this)
 *
 * File: assets/js/story/story-module-mapscroll.js
 */

(function (window) {
  'use strict';

  // ---- Debug logging ----
  var DEBUG = false;
  function log() {
    if (DEBUG && window.console) {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[MapScroll]');
      console.log.apply(console, args);
    }
  }

  // Enable debug via URL param
  if (window.location && window.location.search.indexOf('debug=story') !== -1) {
    DEBUG = true;
  }


  /* ========================================
     Constructor
     ======================================== */

  function StoryMapScroll(core) {
    this.core = core;
    this.overlay = null;
    this.zoomwrap = null;
    this.canvas = null;
    this.svgEl = null;
    this.allLayers = [];
    this.containerPanel = null;
    this.containerIndex = -1;
    this.sequenceIndices = {};
    this.canvasDims = { left: 0, top: 0, width: 0, height: 0 };
    this.naturalW = 0;
    this.naturalH = 0;
    this.currentTransition = null;
    this._delayedTimer = null;

    // Bound handlers
    this._onPanelActivated = this.handlePanelActivated.bind(this);
    this._onResize = null;

    this.init();
  }


  /* ========================================
     Init
     ======================================== */

  StoryMapScroll.prototype.init = function () {
    if (!this.core || !this.core.root) return;

    var root = this.core.root;

    // 1. Find container panel (has data-mapscroll attribute)
    var container = root.querySelector('[data-mapscroll]');
    if (!container) {
      log('No [data-mapscroll] found, module inactive');
      return;
    }

    // Walk up to find the actual panel element
    this.containerPanel = this.findPanel(container);
    if (!this.containerPanel) {
      log('Container not inside a panel, aborting');
      return;
    }

    log('Container found');

    // 2. Parse config from data attributes
    var basemapStr = container.dataset.msBasemap || '';
    var naturalStr = container.dataset.msNatural || '';
    var basemapUrls = basemapStr.split(',').filter(Boolean);
    var naturalParts = naturalStr.split(',');

    this.naturalW = parseFloat(naturalParts[0]) || 1;
    this.naturalH = parseFloat(naturalParts[1]) || 1;

    log('Natural dimensions:', this.naturalW, '×', this.naturalH);
    log('Basemap URLs:', basemapUrls.length);

    // 3. Find SVG in container
    this.svgEl = container.querySelector('svg');
    if (!this.svgEl) {
      log('No <svg> found in container');
      return;
    }

    // 3b. Mobile: swap basemap URLs to smaller versions + fix deprecated xlink:href
    this.isMobile = window.innerWidth <= 768;
    if (this.isMobile) {
      this.swapMobileBasemaps();
    }

    // 4. Create overlay structure
    this.createOverlay(basemapUrls);

    // 5. Move SVG into overlay
    this.svgEl.parentNode.removeChild(this.svgEl);
    var svgWrap = this.overlay.querySelector('.ms-svg-overlay');
    svgWrap.appendChild(this.svgEl);

    // 5b. Preload basemap images (CDN WebPs) — start downloads early
    this.preloadImages();

    // 6. Collect switchable SVG layers
    var selectors = '[id^="vs-hl-"], [id^="vs-lbl-"], [id^="vs-callout-"], [id^="vs-info-"], [id^="vs-base-"]';
    this.allLayers = Array.prototype.slice.call(this.svgEl.querySelectorAll(selectors));
    log('Found', this.allLayers.length, 'switchable layers');

    // 7. Size the canvas wrapper
    this.resizeCanvas();

    // 8. Build panel sequence
    this.buildSequence(root);

    // 9. Register event listeners
    document.addEventListener('story:panel:activated', this._onPanelActivated);

    // 10. Resize handler (debounced)
    var self = this;
    var resizeTimer = null;
    this._onResize = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        self.zoomwrap.classList.add('ms-zoomwrap--immediate');
        self.resizeCanvas();
        // Re-apply current state if zoomed
        self.reapplyCurrentState();
        requestAnimationFrame(function () {
          self.zoomwrap.classList.remove('ms-zoomwrap--immediate');
        });
      }, 150);
    };
    window.addEventListener('resize', this._onResize);

    // 11. Recompute canvas after layout stable
    var self2 = this;
    requestAnimationFrame(function () {
      self2.resizeCanvas();
    });

    // 12. Register with core
    this.core.registerModule('mapscroll', this);

    log('Initialized with', Object.keys(this.sequenceIndices).length, 'panels in sequence');
  };


  /* ========================================
     Create Overlay DOM
     ======================================== */

  StoryMapScroll.prototype.createOverlay = function (basemapUrls) {
    // Overlay (fixed fullscreen)
    this.overlay = document.createElement('div');
    this.overlay.className = 'ms-overlay';

    // Zoomwrap (transform target)
    this.zoomwrap = document.createElement('div');
    this.zoomwrap.className = 'ms-zoomwrap';

    // Canvas (JS-sized aspect-ratio wrapper)
    this.canvas = document.createElement('div');
    this.canvas.className = 'ms-canvas';

    // Basemap images
    for (var i = 0; i < basemapUrls.length; i++) {
      var img = document.createElement('img');
      img.className = 'ms-basemap';
      img.style.zIndex = i + 1;
      img.src = basemapUrls[i].trim();
      img.alt = '';
      img.draggable = false;
      img.decoding = 'async';
      this.canvas.appendChild(img);
    }

    // SVG overlay container
    var svgWrap = document.createElement('div');
    svgWrap.className = 'ms-svg-overlay';
    svgWrap.style.zIndex = basemapUrls.length + 1;
    this.canvas.appendChild(svgWrap);

    // Assemble
    this.zoomwrap.appendChild(this.canvas);
    this.overlay.appendChild(this.zoomwrap);
    document.body.appendChild(this.overlay);
  };


  /* ========================================
     Canvas Wrapper Sizing
     Replicates object-fit:contain via JS
     ======================================== */

  StoryMapScroll.prototype.resizeCanvas = function () {
    if (!this.overlay) return;

    var stageW = window.innerWidth;
    var stageH = window.innerHeight;

    var fitScale = Math.min(stageW / this.naturalW, stageH / this.naturalH);
    var w = this.naturalW * fitScale;
    var h = this.naturalH * fitScale;
    var left = (stageW - w) / 2;
    var top = (stageH - h) / 2;

    this.canvas.style.left = left + 'px';
    this.canvas.style.top = top + 'px';
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';

    this.canvasDims = { left: left, top: top, width: w, height: h };

    log('Canvas resized:', Math.round(w), '×', Math.round(h), 'at', Math.round(left), ',', Math.round(top));
  };


  /* ========================================
     Zoom-to-Focus
     Maps SVG coordinates to screen pixels
     ======================================== */

  StoryMapScroll.prototype.computeZoomTransform = function (focusX, focusY, scale) {
    var stageW = window.innerWidth;
    var stageH = window.innerHeight;
    var d = this.canvasDims;

    // Focus point in canvas-local coords
    var localX = (focusX / this.naturalW) * d.width;
    var localY = (focusY / this.naturalH) * d.height;

    // Focus point in stage coords
    var stageX = d.left + localX;
    var stageY = d.top + localY;

    // Translate so focus point is centered after scale
    var tx = (stageW / 2) - stageX * scale;
    var ty = (stageH / 2) - stageY * scale;

    return { tx: tx, ty: ty, scale: scale };
  };


  /* ========================================
     Apply Transform
     ======================================== */

  StoryMapScroll.prototype.applyTransform = function (tx, ty, scale) {
    var zw = this.zoomwrap;
    var self = this;

    var changed = (Math.round(tx) !== Math.round(this._prevTx || 0)) ||
                  (Math.round(ty) !== Math.round(this._prevTy || 0)) ||
                  (scale !== (this._prevScale || 1));

    this._prevTx = tx;
    this._prevTy = ty;
    this._prevScale = scale;

    // Set CSS variables (always)
    zw.style.setProperty('--ms-tx', tx + 'px');
    zw.style.setProperty('--ms-ty', ty + 'px');
    zw.style.setProperty('--ms-scale', scale);

    if (changed) {
      // Zoom/pan is changing: enable GPU compositing for smooth animation
      zw.style.willChange = 'transform';

      // Reset will-change after idle so browser re-rasterizes SVG
      // at new zoom level (crisp text + paths).
      // Mobile: shorter delay, instant transitions = no flash risk.
      clearTimeout(self._wcTimer);
      var wcDelay = self.isMobile ? 1500 : 3000;
      self._wcTimer = setTimeout(function () {
        zw.style.willChange = 'auto';
        log('will-change reset (SVG re-rasterize)');
      }, wcDelay);
    }
    // If !changed (layer-only switch): don't touch will-change.
    // SVG stays crisp, no GPU layer re-creation needed.

    log('Transform: tx=' + Math.round(tx), 'ty=' + Math.round(ty), 'scale=' + scale, changed ? '(zoom)' : '(layers)');
  };


  /* ========================================
     Apply Layers (show/hide SVG groups)
     ======================================== */

  StoryMapScroll.prototype.applyLayers = function (layerIds) {
    // Build set of target IDs for fast lookup
    var targetSet = {};
    for (var j = 0; j < layerIds.length; j++) {
      var tid = layerIds[j].trim();
      if (tid) targetSet[tid] = true;
    }

    // Auto-couple: vs-base-map-2 always follows vs-base-map
    if (targetSet['vs-base-map']) {
      targetSet['vs-base-map-2'] = true;
    }

    // Separate basemap layers from overlay layers.
    // Basemap removal is DELAYED so the incoming basemap reaches full
    // opacity before the outgoing one starts fading → no background flash.
    var basemapsToRemove = [];

    for (var i = 0; i < this.allLayers.length; i++) {
      var layer = this.allLayers[i];
      if (targetSet[layer.id]) continue;          // Stays active
      if (!layer.classList.contains('is-active')) continue; // Already hidden

      if (layer.id.indexOf('vs-base-') === 0) {
        basemapsToRemove.push(layer);              // Delay removal
      } else {
        layer.classList.remove('is-active');        // Overlay: immediate
      }
    }

    // Add is-active to specified layers (immediate — starts fade-in)
    for (var k = 0; k < layerIds.length; k++) {
      var id = layerIds[k].trim();
      if (!id) continue;

      var el = document.getElementById(id);
      if (el) {
        el.classList.add('is-active');
      } else {
        log('Layer not found:', id);
      }
    }

    // Activate auto-coupled layer
    if (targetSet['vs-base-map-2']) {
      var map2El = document.getElementById('vs-base-map-2');
      if (map2El) map2El.classList.add('is-active');
    }

    // Delayed basemap removal: wait for incoming basemap to reach full
    // opacity, THEN start fading out the old one.
    // Desktop: 1200ms (matches vs-base-* transition), Mobile: 300ms
    clearTimeout(this._basemapRemoveTimer);
    if (basemapsToRemove.length > 0) {
      var delay = this.isMobile ? 300 : 1200;
      this._basemapRemoveTimer = setTimeout(function () {
        for (var b = 0; b < basemapsToRemove.length; b++) {
          basemapsToRemove[b].classList.remove('is-active');
        }
        log('Delayed basemap removal:', basemapsToRemove.length, 'layers');
      }, delay);
    }
  };


  /* ========================================
     Panel Activated Handler
     ======================================== */

  StoryMapScroll.prototype.handlePanelActivated = function (e) {
    var detail = e.detail;
    var panel = detail.panel;
    var index = detail.index;

    // Cancel pending timers from previous step
    clearTimeout(this._wcTimer);
    clearTimeout(this._basemapRemoveTimer);

    // Pre-set will-change before any transform to avoid GPU layer recreation gap
    if (this.zoomwrap) {
      this.zoomwrap.style.willChange = 'transform';
    }

    // Check if this panel is in our sequence
    if (!this.sequenceIndices.hasOwnProperty(index)) {
      // Outside sequence — hide overlay
      if (this.overlay.classList.contains('is-active')) {
        // Hub panel: delay overlay removal so it masks the snap-scroll slide.
        // Both MapScroll (vs-base-hub) and Hub panel show the same WebP,
        // so keeping the overlay briefly makes the transition invisible.
        if (panel.hasAttribute('data-hub')) {
          var self = this;
          clearTimeout(this._hubDelayTimer);
          // Fade out overlay without zoom-to-cover snap.
          // The brief delay masks the snap-scroll slide to hub.
          this._hubDelayTimer = setTimeout(function () {
            self.overlay.classList.remove('is-active');
            self.zoomwrap.style.willChange = 'auto';
            log('Overlay hidden (delayed for hub transition)');
          }, 400);
        } else {
          this.overlay.classList.remove('is-active');
          this.zoomwrap.style.willChange = 'auto';
          log('Overlay hidden (panel', index, 'not in sequence)');
        }
      }
      return;
    }

    // Show overlay — if entering from outside (e.g. scrolling back from hub),
    // show immediately without CSS fade to avoid transparent gap.
    // Also snap zoom instantly so the overlay doesn't show stale cover-zoom.
    clearTimeout(this._hubDelayTimer);
    var enteringFromOutside = !this.overlay.classList.contains('is-active');
    if (enteringFromOutside) {
      this.zoomwrap.classList.add('ms-zoomwrap--immediate');
      this.overlay.style.transition = 'none';
      this.overlay.classList.add('is-active');
      void this.overlay.offsetHeight; // force reflow
      this.overlay.style.transition = '';
      log('Overlay shown (instant, entering from outside)');
    }

    // Read config from panel (or its data-ms child)
    var configEl = panel.querySelector('[data-ms-step]') ||
                   panel.querySelector('[data-mapscroll]') ||
                   panel;

    // Parse layers
    var layers = (configEl.dataset.msLayers || '').split(',').filter(Boolean);

    // Parse focus — mobile override takes priority on small screens
    var focusStr = (this.isMobile && configEl.dataset.msMobileFocus)
                   ? configEl.dataset.msMobileFocus
                   : (configEl.dataset.msFocus || '');
    var focusParts = focusStr.split(',');

    // Parse custom transition
    var transition = configEl.dataset.msTransition || null;
    if (transition) {
      this.setTransitionDuration(parseFloat(transition));
    } else if (this.currentTransition !== null) {
      this.resetTransitionDuration();
    }

    // Apply layers
    this.applyLayers(layers);

    // Apply zoom
    if (focusStr === 'cover') {
      // Match background-size:cover framing (fills viewport, crops excess)
      var t = this.computeCoverTransform();
      this.applyTransform(t.tx, t.ty, t.scale);
    } else if (focusParts.length >= 3) {
      var fx = parseFloat(focusParts[0]);
      var fy = parseFloat(focusParts[1]);
      var fs = parseFloat(focusParts[2]);
      if (!isNaN(fx) && !isNaN(fy) && !isNaN(fs)) {
        var t = this.computeZoomTransform(fx, fy, fs);
        this.applyTransform(t.tx, t.ty, t.scale);
      } else {
        this.applyTransform(0, 0, 1);
      }
    } else {
      // Overview (no zoom)
      this.applyTransform(0, 0, 1);
    }

    // Delayed layers (e.g. data-ms-layers-delayed="vs-info-08" data-ms-delay="2000")
    // Optional: data-ms-layers-remove-delayed — layers to HIDE after delay
    clearTimeout(this._delayedTimer);
    var delayedStr = configEl.dataset.msLayersDelayed || '';
    var removeStr = configEl.dataset.msLayersRemoveDelayed || '';
    if (delayedStr || removeStr) {
      var delayMs = parseInt(configEl.dataset.msDelay, 10) || 2000;
      var delayedIds = delayedStr ? delayedStr.split(',').filter(Boolean) : [];
      var removeIds = removeStr ? removeStr.split(',').filter(Boolean) : [];
      var self = this;
      this._delayedTimer = setTimeout(function () {
        for (var k = 0; k < delayedIds.length; k++) {
          var el = document.getElementById(delayedIds[k].trim());
          if (el) el.classList.add('is-active');
        }
        for (var r = 0; r < removeIds.length; r++) {
          var rem = document.getElementById(removeIds[r].trim());
          if (rem) rem.classList.remove('is-active');
        }
        if (delayedStr) log('Delayed layers shown:', delayedStr);
        if (removeStr) log('Delayed layers hidden:', removeStr);
      }, delayMs);
    }

    // Re-enable transitions after instant snap (next frame so browser paints first)
    if (enteringFromOutside) {
      var self = this;
      requestAnimationFrame(function () {
        self.zoomwrap.classList.remove('ms-zoomwrap--immediate');
      });
    }

    // Store current state for resize reapply
    this._currentLayers = layers;
    this._currentFocus = focusStr;

    log('Step activated: panel', index, 'layers:', layers.join(','), 'focus:', focusStr);
  };


  /* ========================================
     Compute Cover Transform
     Zooms canvas from contain to cover framing,
     matching background-size:cover on the hub panel.
     ======================================== */

  StoryMapScroll.prototype.computeCoverTransform = function () {
    var stageW = window.innerWidth;
    var stageH = window.innerHeight;
    var containScale = Math.min(stageW / this.naturalW, stageH / this.naturalH);
    var coverScale = Math.max(stageW / this.naturalW, stageH / this.naturalH);
    var zoom = coverScale / containScale;
    return this.computeZoomTransform(this.naturalW / 2, this.naturalH / 2, zoom);
  };


  /* ========================================
     Reapply Current State (after resize)
     ======================================== */

  StoryMapScroll.prototype.reapplyCurrentState = function () {
    if (!this._currentFocus && this._currentFocus !== '') return;

    if (this._currentFocus === 'cover') {
      var t = this.computeCoverTransform();
      this.applyTransform(t.tx, t.ty, t.scale);
      return;
    }

    var focusParts = (this._currentFocus || '').split(',');
    if (focusParts.length >= 3) {
      var fx = parseFloat(focusParts[0]);
      var fy = parseFloat(focusParts[1]);
      var fs = parseFloat(focusParts[2]);
      if (!isNaN(fx) && !isNaN(fy) && !isNaN(fs)) {
        var t = this.computeZoomTransform(fx, fy, fs);
        this.applyTransform(t.tx, t.ty, t.scale);
        return;
      }
    }
    this.applyTransform(0, 0, 1);
  };


  /* ========================================
     Transition Duration Helpers
     ======================================== */

  StoryMapScroll.prototype.setTransitionDuration = function (seconds) {
    this.currentTransition = seconds;
    if (this.zoomwrap) {
      this.zoomwrap.style.transitionDuration = seconds + 's';
    }
    for (var i = 0; i < this.allLayers.length; i++) {
      this.allLayers[i].style.transitionDuration = seconds + 's';
    }
  };

  StoryMapScroll.prototype.resetTransitionDuration = function () {
    this.currentTransition = null;
    if (this.zoomwrap) {
      this.zoomwrap.style.transitionDuration = '';
    }
    for (var i = 0; i < this.allLayers.length; i++) {
      this.allLayers[i].style.transitionDuration = '';
    }
  };


  /* ========================================
     Build Sequence (panel index tracking)
     ======================================== */

  StoryMapScroll.prototype.buildSequence = function (root) {
    var panels = this.core.panels || Array.prototype.slice.call(
      root.querySelectorAll('.story-panel, [data-panel]')
    );

    // Find container panel index
    for (var i = 0; i < panels.length; i++) {
      if (panels[i] === this.containerPanel) {
        this.containerIndex = i;
        this.sequenceIndices[i] = this.containerPanel;
        break;
      }
    }

    // Find all step panels
    var steps = root.querySelectorAll('[data-ms-step]');
    for (var s = 0; s < steps.length; s++) {
      var stepPanel = this.findPanel(steps[s]);
      if (!stepPanel) continue;

      for (var p = 0; p < panels.length; p++) {
        if (panels[p] === stepPanel) {
          this.sequenceIndices[p] = stepPanel;
          break;
        }
      }
    }

    log('Sequence: container at', this.containerIndex, '+', Object.keys(this.sequenceIndices).length - 1, 'steps');
  };


  /* ========================================
     Find parent panel element
     ======================================== */

  StoryMapScroll.prototype.findPanel = function (el) {
    var current = el;
    while (current) {
      if (current.classList && (
        current.classList.contains('story-panel') ||
        current.hasAttribute('data-panel')
      )) {
        return current;
      }
      if (current.parentNode && current.parentNode === (this.core && this.core.root)) {
        return current;
      }
      current = current.parentNode;
    }
    return el;
  };


  /* ========================================
     Preload Basemap Images
     ======================================== */

  StoryMapScroll.prototype.preloadImages = function () {
    var images = this.svgEl.querySelectorAll('image');
    var total = 0;
    var loaded = 0;
    var self = this;

    // Mobile: lightweight preload (Image objects only, no <link rel="preload">).
    // Triggers background download of smaller mobile basemaps without the
    // aggressive priority that caused iOS memory crashes with full-res images.
    if (this.isMobile) {
      var mobilePreloaded = 0;
      for (var m = 0; m < images.length; m++) {
        var mHref = images[m].getAttributeNS('http://www.w3.org/1999/xlink', 'href') ||
                    images[m].getAttribute('href');
        if (mHref && mHref.indexOf('http') === 0) {
          var mImg = new Image();
          mImg.src = mHref;
          mobilePreloaded++;
        }
      }
      self.overlay.classList.add('ms-loaded');
      log('Mobile: lightweight preload for', mobilePreloaded, 'basemap images');
      return;
    }

    function onDone() {
      loaded++;
      if (loaded >= total) {
        self.overlay.classList.add('ms-loaded');
        log('All', total, 'basemap images loaded');
      }
    }

    for (var i = 0; i < images.length; i++) {
      var href = images[i].getAttributeNS('http://www.w3.org/1999/xlink', 'href') ||
                 images[i].getAttribute('href');
      if (href && href.indexOf('http') === 0) {
        total++;
        // Preload link for early high-priority download
        var link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = href;
        document.head.appendChild(link);
        // Image object for reliable decode tracking
        var img = new Image();
        img.onload = onDone;
        img.onerror = onDone;
        img.src = href;
      }
    }

    if (total === 0) {
      self.overlay.classList.add('ms-loaded');
    }
    log('Preloading', total, 'basemap images');
  };


  /* ========================================
     Mobile Basemap Swap
     Replaces _o.webp (7355×4476, exceeds iOS 4096px GPU limit)
     with standard .webp (smaller) in SVG image elements.
     Also migrates deprecated xlink:href → href.
     ======================================== */

  StoryMapScroll.prototype.swapMobileBasemaps = function () {
    var images = this.svgEl.querySelectorAll('image');
    var swapped = 0;

    for (var i = 0; i < images.length; i++) {
      var img = images[i];
      var href = img.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ||
                 img.getAttribute('href');
      if (!href) continue;

      // Swap _o.webp → .webp (smaller mobile version)
      var newHref = href;
      if (href.indexOf('_o.webp') !== -1) {
        newHref = href.replace('_o.webp', '.webp');
        swapped++;
      }

      // Migrate xlink:href → href (xlink deprecated since SVG 2)
      img.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
      img.setAttribute('href', newHref);
    }

    log('Mobile: swapped', swapped, 'basemap URLs (_o.webp → .webp)');
  };


  /* ========================================
     Destroy
     ======================================== */

  StoryMapScroll.prototype.destroy = function () {
    document.removeEventListener('story:panel:activated', this._onPanelActivated);

    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }

    clearTimeout(this._delayedTimer);
    clearTimeout(this._wcTimer);
    clearTimeout(this._hubDelayTimer);
    clearTimeout(this._basemapRemoveTimer);

    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }

    this.overlay = null;
    this.zoomwrap = null;
    this.canvas = null;
    this.svgEl = null;
    this.allLayers = [];
    this.sequenceIndices = {};

    log('Destroyed');
  };


  /* ========================================
     Expose to window
     ======================================== */

  window.StoryModuleMapScroll = StoryMapScroll;

})(window);
